import React, { useEffect, useState } from "react";
import { Box, lighten, Typography } from "@mui/material";
import { Components } from "react-markdown";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import { Message, MessageTypeColor } from "../../../../types";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { useTextMessageStyles } from "./styles";
import { useAppStore } from "../../../../stores/appStore";
import { useChatStore } from "../../../../stores/chatStore";
import { FeedbackMessage } from "./feetback-message/feetback-message";
import { hcpLabelsForTokens } from "../../../../utils/hcpMentions";
import { renderHcpMentionText } from "./hcp-mention-text";

export interface TextMessageProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  nextMessage?: Message;
  setLargeHigth: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TextMessage: React.FC<TextMessageProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  nextMessage,
  setLargeHigth,
  handleRemoveTag,
}) => {
  const { isDark } = useAppStore();
  const { setTargetMessageId } = useChatStore();
  const styles = useTextMessageStyles(isBot, variant);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return nextMessage ? false : !!message.collapse;
  });
  const [largeHigth, setLargeHigthState] = useState(true);

  useEffect(() => {
    if (nextMessage) {
      setIsCollapsed(true);
    } else if (message.collapse !== undefined) {
      setIsCollapsed(!message.collapse);
    }
  }, [message.collapse, nextMessage]);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
    setLargeHigthState((prev) => !prev);
    setLargeHigth((prev: boolean) => !prev);
  };

  const text = message.text || "";
  const title = message.title || "";
  const replyTargetId = message.reply_message_id?.trim() || null;
  const replyTargetTabId = message.reply_message_tab_id?.trim() || null;
  const replySnippet =
    message.reply_message_text?.trim() || message.reply?.trim() || "";
  const showReplyStrip = !!(replyTargetId || replySnippet);

  if (!text && !title && !showReplyStrip) return null;
  const shortText = text.length > 300 ? `${text.substring(0, 300)}...` : text;

  const hasAvatarImage = (text: string): boolean => {
    if (!text) return false;

    const hasCircularShape = /border-radius\s*:\s*50%/.test(text);
    const hasAvatarSize = /(?:width|height)\s*:\s*(?:[6-9]\d|100)\s*px/.test(
      text,
    );
    const hasImgTag = /<img[^>]*>/i.test(text);

    return hasImgTag && hasCircularShape && hasAvatarSize;
  };

  const containsAvatar = hasAvatarImage(text) || hasAvatarImage(title);

  const isSystemMessage = message.message_type === "system";

  const customComponents: Components = {
    code: (props) => {
      const { children, className } = props;

      return (
        <span
          className={`custom-formatted-text ${className || ""}`}
          style={{
            fontFamily: "monospace",
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            padding: "0.2em 0.4em",
            borderRadius: "3px",
          }}
        >
          {children}
        </span>
      );
    },

    pre: (props) => {
      const { children } = props;

      return (
        <div
          className="custom-formatted-block"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            padding: "1em",
            borderRadius: "4px",
            overflowX: "auto",
          }}
        >
          {children}
        </div>
      );
    },

    a: ({ children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: "underline",
          color: "#FF7961",
          cursor: "pointer",
        }}
      >
        {children}
      </a>
    ),
    img: ({ src, alt, style, ...props }) => {
      const imageSrc = src ?? "";
      const isLoaderGif = /(?:^|\/)(?:animation)?loading2\.gif(?:$|\?)/i.test(
        imageSrc,
      );
      const shouldAlignAboveAvatar = isSystemMessage && isLoaderGif;

      return (
        <Box
          component="img"
          src={src}
          alt={alt || "Image"}
          {...props}
          style={
            shouldAlignAboveAvatar
              ? {
                  ...(style as React.CSSProperties | undefined),
                  width: undefined,
                  maxWidth: undefined,
                  height: undefined,
                  maxHeight: undefined,
                }
              : style
          }
          sx={{
            maxWidth: shouldAlignAboveAvatar
              ? { xs: "96px", sm: "110px", md: "120px" }
              : "100%",
            maxHeight: shouldAlignAboveAvatar
              ? { xs: "96px", sm: "110px", md: "120px" }
              : undefined,
            height: "auto",
            display: "block",
            objectFit: "contain",
            mt: 1,
            mb: 1,
            ml: shouldAlignAboveAvatar ? { xs: 0.25, sm: 0.5 } : 0,
            mr: shouldAlignAboveAvatar ? "auto" : 0,
            transform: shouldAlignAboveAvatar ? "translateX(-20px)" : "none",
          }}
        />
      );
    },
  };

  if (message.message_type === "feedback") {
    return (
      <FeedbackMessage
        message={message}
        currentReaction={currentReaction}
        isSaved={isSaved}
        markedIds={markedIds}
        isTags={isTags}
        handleReactionClick={handleReactionClick}
        handleAddToFavorite={handleAddToFavorite}
        isBot={isBot}
        variant={variant}
        handleRemoveTag={handleRemoveTag}
      />
    );
  }

  const hasHcpMentions = Boolean(
    message.mentionToken || message.mentionTokens?.length,
  );

  const renderBodyText = (body: string) =>
    hasHcpMentions && !isBot ? (
      <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {renderHcpMentionText(
          body,
          hcpLabelsForTokens(
            message.mentionTokens?.length
              ? message.mentionTokens
              : message.mentionToken
                ? [message.mentionToken]
                : [],
          ),
        )}
      </Box>
    ) : (
      <CustomMarkdown components={customComponents} children={body} />
    );

  const customMarkdownStyle = {
    wordBreak: "break-word",
    "& ul": {
      margin: 0,
      marginBottom: "1em",
    },
  };

  const adjustedBgLighten =
    styles.container.background !== "inherit" &&
    !styles.container.background.startsWith("linear-gradient")
      ? lighten(styles.container.background, 0.2)
      : styles.container.background;

  // If message contains avatar image or is system message, disable collapse functionality
  if (message.collapse === undefined || containsAvatar || isSystemMessage) {
    return (
      <Box sx={styles.container}>
        {title && (
          <Box sx={styles.titleRow}>
            <Typography sx={{ fontWeight: "bold" }} variant="h6">
              <CustomMarkdown components={customComponents} children={title} />
            </Typography>
          </Box>
        )}
        {text && (
          <Box sx={styles.messageRow}>
            <Box sx={customMarkdownStyle}>
              {renderBodyText(text)}
            </Box>
          </Box>
        )}

        {showReplyStrip && (
          <Box
            onClick={() => setTargetMessageId(replyTargetId, replyTargetTabId)}
            sx={{
              position: "relative",
              background: adjustedBgLighten,
              alignItems: "stretch",
              borderTopRightRadius: "10px",
              borderBottomRightRadius: "10px",
              borderTopLeftRadius: "5px",
              borderBottomLeftRadius: "5px",
              mb: 1,
              cursor: "pointer",
              pl: "12px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "6px",
                background: "#ffffff",
                borderTopLeftRadius: "10px",
                borderBottomLeftRadius: "10px",
              }}
            />
            <Typography sx={{ padding: "10px", flex: 1 }}>
              <Box
                sx={{
                  ...customMarkdownStyle,
                  ...(isCollapsed ? styles.collapsedReplySnippet : {}),
                }}
              >
                {replySnippet ? (
                  <CustomMarkdown
                    components={customComponents}
                    children={replySnippet}
                  />
                ) : (
                  "View original message"
                )}
              </Box>
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <ReactionAndFavorite
            currentReaction={currentReaction}
            markedIds={markedIds}
            isSaved={isSaved}
            isTags={isTags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            reactionBg={styles.container.background}
            handleRemoveTag={handleRemoveTag}
          />
          <Typography variant="caption" sx={styles.timestamp}>
            <span>{format(new Date(message.date), "dd/MM/yy")}</span>
            <span style={{ margin: "0 5px" }} />
            <span>{format(new Date(message.date), "HH:mm")}</span>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.collapseContainer}>
      <Box sx={customMarkdownStyle}>
        {title && (
          <Typography variant="h6" sx={{ mb: "12px", fontWeight: "bold" }}>
            <CustomMarkdown components={customComponents} children={title} />
          </Typography>
        )}
        {showReplyStrip && (
          <Box
            onClick={() => setTargetMessageId(replyTargetId, replyTargetTabId)}
            sx={{
              position: "relative",
              background: adjustedBgLighten,
              alignItems: "stretch",
              borderTopRightRadius: "10px",
              borderBottomRightRadius: "10px",
              borderTopLeftRadius: "5px",
              borderBottomLeftRadius: "5px",
              mb: 1,
              cursor: "pointer",
              pl: "12px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "6px",
                background: "#ffffff",
                borderTopLeftRadius: "10px",
                borderBottomLeftRadius: "10px",
              }}
            />
            <Typography sx={{ padding: "10px", flex: 1, }}>
              <Box
                sx={{
                  ...customMarkdownStyle,
                  ...(isCollapsed ? styles.collapsedReplySnippet : {}),
                }}
              >
                {replySnippet ? (
                  <CustomMarkdown
                    components={customComponents}
                    children={replySnippet}
                  />
                ) : (
                  "View original message"
                )}
              </Box>
            </Typography>
          </Box>
        )}
        {!!text && renderBodyText(isCollapsed ? shortText : text)}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            opacity: 0.8,
            justifyContent: "space-between",
          }}
        >
          <Box sx={styles.collapseToggle} onClick={handleToggleCollapse}>
            <Typography variant="body2" sx={styles.collapseToggleText}>
              {isCollapsed ? "More" : "Less"}
            </Typography>
            <ExpandMoreIcon sx={styles.collapseToggleIcon(isCollapsed)} />
          </Box>
          <Typography variant="caption" sx={styles.timestamp}>
            <span>{format(new Date(message.date), "dd/MM/yy")}</span>
            <span style={{ margin: "0 5px" }} />
            <span>{format(new Date(message.date), "HH:mm")}</span>
          </Typography>
        </Box>

        <ReactionAndFavorite
          currentReaction={currentReaction}
          markedIds={markedIds}
          isTags={isTags}
          isSaved={isSaved}
          handleReactionClick={handleReactionClick}
          handleAddToFavorite={handleAddToFavorite}
          reactionBg={styles.container.background}
          handleRemoveTag={handleRemoveTag}
        />
      </Box>
    </Box>
  );
};
